import {
  Typography,
  useTheme,
  CircularProgress,
  DialogActions,
  TextField,
  DialogContent,
  Grid,
  Tooltip,
} from '@mui/material';
import { type GridColumns, type GridValidRowModel } from '@mui/x-data-grid';
import { AlertModal, CustomDataGrid, DialogBox } from 'components';
import { colorTheme } from 'theme';
import { useEffect, useState } from 'react';
import Buttons from 'components/elements/Buttons';
import { setSnackBarFailed, setSnackBarSuccess } from 'store/snackBar';
import { useDispatch, useSelector } from 'react-redux';
import { strings } from 'i18n/Strings';
import { getAllUserSalesUnit } from 'service/UserSalesUnitService';
import {
  confirmationIBorder,
  createAndPlaceSalesOrder,
  getEmployeeDetailsByEmailOrUniqueId,
  transferOutUserUnits,
} from 'service/OrderService';
import { ErrorModel, YesNoModal } from 'components/shared';
import { salesOrderConstants } from 'constants/constantText';
import { UnitFormater } from 'utils/GeneralUtil';
import type { SearchUserDataType } from '../TodayOrder';
import { setCurrentPage } from 'store/component/pagination';
import { zpUserUniqueId } from 'utils/Constants';
import SalesOrderDetails from './SalesOrderDetails';
import { setIsFundOrderProcessing } from 'store/fund';

export interface TodayOrderType {
  userFundId: number;
  fundId: number;
  contractId: number;
  fundName: string;
  Symbol: string;
  totalContribution: string;
}

interface UserSalesUint {
  userSalesId: number;
  withDrawRequestId: number;
  userId: number;
  planId: number;
  units: string;
  fundId: number;
  orderId: number | null;
  transactionId: number | null;
  createdBy: number | null;
  updatedBy: number | null;
  createdAt: string;
  updatedAt: string;
  fundInfo: FundInfo;
}

interface FundInfo {
  name: string;
  symbol: string;
  contractId: number;
}

interface UserSalesUintType {
  userSalesId: number;
  requestId: number;
  userId: number;
  planId: number;
  units: string;
  fundId: number;
  orderId: number | null;
  transactionId: number | null;
  createdBy: number;
  updatedBy: number;
  createdAt: string;
  updatedAt: string;
  fundInfo: FundInfo;
  orderMethod: string;
}

interface FundInfo {
  name: string;
  symbol: string;
  contractId: number;
}

export interface SalesUintResponse {
  userSalesUint: UserSalesUint[];
}

const SalesOrder = (): JSX.Element => {
  const theme = useTheme();
  const colors = colorTheme(theme.palette.mode);
  const [salesUnitData, setSalesUnitData] = useState<SalesUintResponse[]>([]);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [openConfirmationSellDialog, setOpenConfirmationSellDialog] =
    useState(false);

  const [orderConfirmation, setOrderConfirmation] = useState([]);
  const [orderData, setOrderData] = useState<TodayOrderType>();
  const [placedFundId, setPlacedFundId] = useState(null);
  const [isYesDisabled, setYesDisabled] = useState(false);
  const dispatch = useDispatch();
  const [pageCount, setPageCount] = useState(null);
  const pageInfo = useSelector((state) => state?.pagination?.salesOrder);
  const [openDialog, setOpenDialog] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [searchedUserData, setSearchedUserData] =
    useState<SearchUserDataType | null>();
  const [ibError, setIbError] = useState<{
    information: string;
    open: boolean;
    title: string;
  }>({ information: '', open: false, title: '' });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [fundRow, setFundRow] = useState([]);
  const [placeOrderLoader, setPlaceOrderLoader] = useState(false);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [transferOutValues, setTransferOutValues] = useState<any>({
    fund1: { id: 1, value: '' },
    fund2: { id: 2, value: '' },
    fund3: { id: 3, value: '' },
    fund4: { id: 4, value: '' },
    fund5: { id: 5, value: '' },
    fund6: { id: 6, value: '' },
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleInputChange2 = (
    e: React.ChangeEvent<HTMLInputElement>,
    fundId: number,
    maxValue: number,
  ) => {
    const value = e.target.value;
    if (!isNaN(Number(value)) && +value <= maxValue) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setTransferOutValues((prevValues: any[]) => ({
        ...prevValues,
        [`fund${fundId}`]: {
          id: fundId,
          value,
        },
      }));
    }
  };

  const handleTransferOutClick = (): void => {
    setOpenDialog(true);
  };

  const handleDialogClose = (): void => {
    setOpenDialog(false);
    setSearchValue('');
    setSearchedUserData(null);
  };
  const handleSearch = async (value: string): Promise<void> => {
    try {
      const searchResult = await getEmployeeDetailsByEmailOrUniqueId(value);
      if (searchResult) {
        setSearchedUserData(searchResult);
        dispatch(setCurrentPage({ key: 'customers', value: 0 }));
      } else {
        setSearchedUserData(null);
        dispatch(
          setSnackBarFailed({
            snackBarMessage: strings.displayText.noUserDataFound,
          }),
        );
      }
    } catch (error) {
      console.log(error);
      throw error;
    }
  };

  const handleTransferOut = async (): Promise<void> => {
    transferOutUserUnits(searchedUserData?.uniqueId, transferOutValues)
      .then(async () => {
        dispatch(
          setSnackBarSuccess({
            snackBarMessage: strings.displayText.transferOutSuccessfully,
          }),
        );
        handleDialogClose();
        await getAllSalesUnits();
        setOpenConfirmationDialog(false);
      })
      .catch((error) => {
        setOpenConfirmationDialog(false);
        if (searchValue === '') {
          dispatch(
            setSnackBarFailed({
              snackBarMessage: strings.displayText.customerIdRequiredText,
            }),
          );
        } else {
          dispatch(
            setSnackBarFailed({
              snackBarMessage: error as string,
            }),
          );
        }
      });
  };

  const getAllSalesUnits = async (): Promise<void> => {
    try {
      setIbError({
        information: '',
        open: false,
        title: '',
      });
      const data = await getAllUserSalesUnit(
        Number(pageInfo?.page) + 1,
        pageInfo?.pageSize,
      );
      setSalesUnitData(data?.order);
      setPageCount(data?.pageCount);
    } catch (error) {
      console.log(error);
    }
  };
  useEffect(() => {
    void getAllSalesUnits();
  }, [pageInfo?.page, pageInfo.pageSize]);
  const onClickePlaceOrder = (orderData: UserSalesUintType): void => {
    setPlacedFundId(orderData?.fundId);
    const orderDataWithMethod = {
      userSalesId: orderData?.userSalesId,
      fundId: orderData?.fundId,
      contractId: orderData?.fundInfo?.contractId,
      fundName: orderData?.fundInfo?.name,
      Symbol: orderData?.fundInfo?.symbol,
      units: orderData?.units,
      orderStatus: salesOrderConstants.sumbmitted,
      orderMethod: salesOrderConstants.sell,
    };

    setOrderData(orderDataWithMethod);
    createAndPlaceSalesOrder(orderDataWithMethod)
      .then(async (res) => {
        setPlaceOrderLoader(false);
        dispatch(setIsFundOrderProcessing(false));
        if (res.error) {
          setIbError({
            information: res.error || '',
            open: true,
            title: strings.displayText.idErrorHeader,
          });
          setOrderConfirmation([]);
          setOpenConfirmationSellDialog(false);
          setPlacedFundId(null);
        } else if (res?.[0]?.order_id) {
          setOrderConfirmation(res);
          void getAllSalesUnits();
          setIbError({
            information: '',
            open: false,
            title: '',
          });
        } else {
          setOrderConfirmation(res);
          setOpenConfirmationSellDialog(true);
        }
        setPlacedFundId(null);
      })
      .catch((err) => {
        setPlaceOrderLoader(false);
        dispatch(setIsFundOrderProcessing(false));
        setOrderConfirmation([]);
        setOpenConfirmationSellDialog(false);
        setIbError({
          information: err?.response?.data?.message || '',
          open: true,
          title: strings.displayText.idErrorHeader,
        });
        setPlacedFundId(null);
      });
  };

  const confirmIBOrder = async (): Promise<void> => {
    setYesDisabled(true);
    const confirmationVal = {
      orderId: orderConfirmation[0].id,
      confirmation: true,
      ...orderData,
    };
    try {
      const data = await confirmationIBorder(confirmationVal);
      if (data.error) {
        setIbError({
          information: data.error || '',
          open: true,
          title: strings.displayText.idErrorHeader,
        });
        setOrderConfirmation([]);
        setOpenConfirmationSellDialog(false);
        setPlacedFundId(null);
      } else if (!data[0]?.isComplete) {
        setOrderConfirmation(data);
        setOpenConfirmationSellDialog(true);
      } else {
        setOpenConfirmationSellDialog(false);
        await getAllSalesUnits();
        dispatch(
          setSnackBarSuccess({
            snackBarMessage: strings.displayText.orderPlacedText,
          }),
        );
      }
    } catch (err: unknown) {
      setOrderConfirmation([]);
      setOpenConfirmationSellDialog(false);
      setIbError({
        information: err?.response?.data?.message || '',
        open: true,
        title: strings.displayText.idErrorHeader,
      });
    }
    setYesDisabled(false);
  };

  const handleClose = (): void => {
    setIsModalOpen(false);
  };

  const columns: GridColumns<GridValidRowModel> = [
    {
      field: salesOrderConstants.name,
      flex: 2,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.fundName}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Tooltip title={row?.fundInfo?.description}>
          <Typography
            variant="h5"
            color={colors.gray[100]}
            fontSize={14}
            sx={{
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            }}
          >
            {row?.fundInfo?.shortDescription}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'Symbol',
      flex: 0.75,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.symbol}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.fundInfo.symbol}
        </Typography>
      ),
    },
    {
      field: 'contractId',
      flex: 0.75,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.contractId}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.fundInfo.contractId}
        </Typography>
      ),
    },
    {
      field: salesOrderConstants.units,
      flex: 0.8,
      align: 'right',
      headerAlign: 'right',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.unit}
        </Typography>
      ),
      renderCell: ({ row }) => {
        const formattedUnits = Number(row.units).toFixed(3);
        const displayUnits =
          Number(formattedUnits) < 0.001 ? '0.001' : formattedUnits;

        return (
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            <a
              style={{
                color: 'blue',
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
              onClick={() => {
                setFundRow(row);
                setIsModalOpen(true);
              }}
            >
              {UnitFormater(displayUnits)}
            </a>
          </Typography>
        );
      },
    },
    {
      field: salesOrderConstants.placeAndPreviewOrder,
      flex: 1,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.placeAndPreviewOrder}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          <Buttons
            sx={{
              cursor: placeOrderLoader ? 'not-allowed' : 'pointer',
              height: 40,
              bgcolor: colors.greenAccent[500],
              borderRadius: 2,
            }}
            onClick={() => {
              if (!placeOrderLoader) {
                setPlaceOrderLoader(true);
                dispatch(setIsFundOrderProcessing(true));
                onClickePlaceOrder(row);
              }
            }}
            endIcon={
              placedFundId === row?.fundId ? (
                <CircularProgress size={15} />
              ) : null
            }
            disabled={placedFundId === row?.fundId}
          >
            {placeOrderLoader && placedFundId === row?.fundId
              ? strings.displayText.placing
              : strings.displayText.placeOrder}
          </Buttons>
        </Typography>
      ),
    },
  ];

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: '10px',
        }}
      >
        <Buttons
          onClick={handleTransferOutClick}
          variant="contained"
          color="primary"
        >
          {strings.displayText.transferOut}
        </Buttons>
      </div>
      <CustomDataGrid
        columns={columns}
        rows={salesUnitData as GridRowData[]}
        getRowId={(row: GridRowData) => `${String(row?.fundId)}`}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        }
        pageCount={pageCount}
        currentPage="salesOrder"
      />
      <DialogBox
        open={openDialog}
        onClose={handleDialogClose}
        title={strings.displayText.transferOut}
        fullWidth
      >
        <DialogContent style={{ display: 'flex', alignItems: 'center' }}>
          <TextField
            label={strings.displayText.enterEmailOrCustomer}
            variant="outlined"
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value);
            }}
            style={{ width: '400px' }}
          />
          <Buttons
            onClick={() => {
              void handleSearch(searchValue.trim());
            }}
            color="primary"
            style={{ marginLeft: '40px' }}
            disabled={searchValue === ''}
          >
            {strings.displayText.search}
          </Buttons>
        </DialogContent>

        <DialogContent>
          <>
            {searchedUserData?.uniqueId === zpUserUniqueId ? (
              <>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: '20px',
                  }}
                >
                  <Grid item xs={6}>
                    <TextField
                      margin="normal"
                      fullWidth
                      label={strings.displayText.firstName}
                      value={searchedUserData?.firstName}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      margin="normal"
                      fullWidth
                      label={strings.displayText.lastName}
                      value={searchedUserData?.lastName}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      margin="normal"
                      fullWidth
                      label={strings.displayText.email}
                      value={searchedUserData?.email}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      margin="normal"
                      fullWidth
                      label={strings.displayText.phoneNumber}
                      value={searchedUserData?.phone}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      margin="normal"
                      fullWidth
                      label={strings.displayText.customersId}
                      value={searchedUserData?.uniqueId}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={6} />
                  {strings.displayText.currentHoldings}
                  <Grid item xs={6} />
                  <Grid item xs={6} />
                  {searchedUserData?.fundDetails?.map((item) => (
                    <>
                      <Grid item xs={6}>
                        <TextField
                          margin="normal"
                          label="Fund ID"
                          value={item?.fundId}
                          InputProps={{ readOnly: true }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Tooltip title={item?.description} placement="top">
                          <TextField
                            margin="normal"
                            fullWidth
                            label="Fund Name"
                            value={item?.description}
                            InputProps={{ readOnly: true }}
                          />
                        </Tooltip>
                      </Grid>
                      <Grid item xs={10}>
                        <TextField
                          margin="normal"
                          label="Units"
                          value={item?.amount}
                          InputProps={{ readOnly: true }}
                        />
                      </Grid>
                    </>
                  ))}

                  {strings.displayText.enterHowMuchUnitstoTransferout}
                  <Grid item xs={6} />
                  <Grid item xs={6} />
                  {searchedUserData?.fundDetails?.map((item) => {
                    const amount = item?.amount;
                    return (
                      <>
                        <Grid item xs={6} key={item?.fundId}>
                          <TextField
                            margin="normal"
                            label="Fund ID"
                            value={item?.fundId}
                            InputProps={{ readOnly: true }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            margin="normal"
                            fullWidth
                            label="Fund Name"
                            value={item?.description}
                            InputProps={{ readOnly: true }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            margin="normal"
                            label="Units"
                            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                            helperText={`Max: ${amount}`}
                            inputProps={{
                              min: 0,
                              max: amount,
                              type: 'number',
                            }}
                            onChange={(e) => {
                              handleInputChange2(e, item?.fundId, amount);
                            }}
                          />
                        </Grid>
                      </>
                    );
                  })}
                </div>
              </>
            ) : (
              searchedUserData && (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: '20px',
                  }}
                >
                  <Grid item xs={6}>
                    <TextField
                      margin="normal"
                      fullWidth
                      label={strings.displayText.firstName}
                      value={searchedUserData?.firstName}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      margin="normal"
                      fullWidth
                      label={strings.displayText.lastName}
                      value={searchedUserData?.lastName}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      margin="normal"
                      fullWidth
                      label={strings.displayText.email}
                      value={searchedUserData?.email}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      margin="normal"
                      fullWidth
                      label={strings.displayText.phoneNumber}
                      value={searchedUserData?.phone}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      margin="normal"
                      fullWidth
                      label={strings.displayText.uniqueId}
                      value={searchedUserData?.uniqueId}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  {searchedUserData?.fundDetails?.map((item) => (
                    <>
                      <Grid item xs={6}>
                        <Tooltip title={item?.description} placement="top">
                          <TextField
                            margin="normal"
                            fullWidth
                            label={item?.description}
                            value={item?.amount ?? '0'}
                            InputProps={{ readOnly: true }}
                          />
                        </Tooltip>
                      </Grid>
                    </>
                  ))}
                </div>
              )
            )}
          </>
        </DialogContent>
        <DialogActions>
          <Buttons
            disabled={
              Number(searchedUserData?.totalUnitsAllocated) === 0 ||
              searchValue === ''
            }
            onClick={() => {
              setOpenConfirmationDialog(true);
            }}
            color="primary"
          >
            {strings.displayText.transferOut}
          </Buttons>
        </DialogActions>
      </DialogBox>
      <AlertModal
        open={openConfirmationDialog}
        onClose={() => {
          setOpenConfirmationDialog(!openConfirmationDialog);
        }}
        title={strings.displayText.areYouConfirm}
        isButtonForConfirmation={true}
        handleConfirm={handleTransferOut}
      />

      {orderConfirmation.length > 0 && !orderConfirmation[0]?.order_id ? (
        <YesNoModal
          open={openConfirmationSellDialog}
          onClose={() => {
            setOpenConfirmationSellDialog(false);
            setOrderConfirmation([]);
          }}
          isDisabled={isYesDisabled}
          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          title={`${orderConfirmation[0].message[0]} ${strings.displayText.orderConfirmAgain}`}
          handleConfirm={confirmIBOrder}
        ></YesNoModal>
      ) : null}

      {ibError.open && (
        <ErrorModel
          open={ibError.open}
          title={ibError.title}
          onClose={() => {
            setIbError({ information: '', open: false, title: '' });
          }}
          information={ibError.information}
        />
      )}

      {isModalOpen && (
        <DialogBox
          open={isModalOpen}
          title="Sell Order Detailed"
          onClose={handleClose}
          fullWidth
        >
          <SalesOrderDetails fundRow={fundRow} />
        </DialogBox>
      )}
    </>
  );
};

export default SalesOrder;
